import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { message } from 'antd';

import { setCulture } from 'services/others';
import { RegisterCustomError } from 'types';

import RegisterForm from '../components/forms/register-form';
import HeaderSecondary from '../components/Header/headerSecondary';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { registerUser } from '../services/auth';
import useTranslation from '../customHooks/translate';
import TermsConditionsForm from './Dialogs/TermsConditionsForm';

const Register = () => {
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const translation = useTranslation();
  const navigate = useNavigate();

  const lang = (localStorage.getItem('language') || 'en').replace(/['"]/g, '');

  useEffect(() => {
    setCulture(lang);
  }, [lang]);

  const toggleModal = () => {
    setIsTermsModalOpen(!isTermsModalOpen);
  };

  const [, setDateInStorage] = useLocalStorage('date', '');
  const [, setEmailInStorage] = useLocalStorage('email', '');
  const [, setLoginInStorage] = useLocalStorage('login', '');
  const [, setPasswordInStorage] = useLocalStorage('password', '');

  const { mutate: registerHandler, isLoading } = useMutation(registerUser, {
    onMutate: () => {
      setPasswordError(null);
      setEmailError(null);
    },
    onSuccess({ data }, { email, login, password }) {
      setEmailInStorage(email);
      setLoginInStorage(login);
      setPasswordInStorage(password);
      setDateInStorage(data);

      navigate('/check-email', { replace: true, state: { date: data, email } });
    },
    onError(error: RegisterCustomError) {
      const apiErrors = error.response?.data?.errors;
      const messageFromApi = error.response?.data?.Message;
      const generalErrorFromApi = apiErrors?.[0]?.message;

      messageFromApi && message.error(messageFromApi);
      generalErrorFromApi && message.error(generalErrorFromApi);
      
      setPasswordError(apiErrors?.Password?.[0] || null);
      setEmailError(apiErrors?.Email?.[0] || null);
    },
  });

  return (
    <section className="box-border grid h-screen grid-cols-1 gap-[60px] overflow-hidden px-[24px] py-[24px]  md:pr-[24px] lg:grid-cols-2 xl:grid-cols-3 xl:gap-[120px] xl:pl-[156px]">
      <div className="col-span-1 flex flex-col gap-[96px]">
        <HeaderSecondary />
        <div className="flex flex-col gap-[24px]">
          <div className="flex flex-col gap-[12px]">
            <h1 className="text-4xl font-bold text-[#222]">
              {translation.register.SignUp}
            </h1>
            <span className="block font-markpro">
              <span className="text-charleston-green">
                {translation.register.SwithtoSignInText}
              </span>{' '}
              <span className="font-medium text-[#6DCE92]">
                <Link to="/login">
                  {translation.register.SwithtoSignInLink}
                </Link>
              </span>
            </span>
          </div>
          <RegisterForm
            isLoading={isLoading}
            registerHandler={registerHandler}
            emailError={emailError}
            passwordError={passwordError}
          />
          <div className="gap-1 text-center text-sm">
            {translation.register.TermsText}
            <div onClick={toggleModal} className="cursor-pointer">
              <span className="font-medium underline">
                &nbsp; {translation.register.TermsLink}
              </span>
            </div>
            <TermsConditionsForm
              open={isTermsModalOpen}
              toggleModal={toggleModal}
            />
          </div>
        </div>
      </div>
      <div className="cols-span-1 hidden h-full overflow-hidden rounded-[24px] lg:block xl:col-span-2">
        <img
          src="assets/login-cover.svg"
          alt="login-cover"
          className="h-full w-full object-cover"
        />
      </div>
    </section>
  );
};

export default Register;
